export type LogLevel = "debug" | "info" | "warn" | "error";

/**
 * Structured logging context
 */
export interface LogContext {
  userId?: string;
  component?: string;
  action?: string;
  entityType?: string;
  entityId?: string;
  duration?: number;
  statusCode?: number;
  [key: string]: unknown;
}

/**
 * PII patterns to redact from logs
 */
const PII_PATTERNS = {
  email: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
  phone: /\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/g,
  ssn: /\b\d{3}-\d{2}-\d{4}\b/g,
  creditCard: /\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g,
  token: /Bearer\s+[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+/g,
};

/**
 * Redact PII from any data structure
 */
function redactPII(data: unknown): unknown {
  if (typeof data === "string") {
    let redacted = data;
    Object.values(PII_PATTERNS).forEach((pattern) => {
      redacted = redacted.replace(pattern, "[REDACTED]");
    });
    return redacted;
  }

  if (Array.isArray(data)) {
    return data.map(redactPII);
  }

  if (typeof data === "object" && data !== null) {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(data)) {
      if (["password", "token", "apiKey", "secret", "authorization"].includes(key.toLowerCase())) {
        result[key] = "[REDACTED]";
      } else {
        result[key] = redactPII(value);
      }
    }
    return result;
  }

  return data;
}

class Logger {
  private isDevelopment = import.meta.env.DEV;

  private formatMessage(level: LogLevel, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString();
    const contextStr = context ? ` ${JSON.stringify(context)}` : "";
    return `[${timestamp}] [${level.toUpperCase()}] ${message}${contextStr}`;
  }

  debug(message: string, context?: LogContext): void {
    if (this.isDevelopment) {
      console.debug(this.formatMessage("debug", message, context));
    }
  }

  info(message: string, context?: LogContext): void {
    const redactedContext = context ? (redactPII(context) as LogContext) : undefined;
    const formatted = this.formatMessage("info", message, redactedContext);
    console.info(formatted);
  }

  warn(message: string, context?: LogContext): void {
    const redactedContext = context ? (redactPII(context) as LogContext) : undefined;
    const formatted = this.formatMessage("warn", message, redactedContext);
    console.warn(formatted);
  }

  error(message: string, error: Error | unknown, context?: LogContext): void {
    const errorDetails =
      error instanceof Error
        ? { name: error.name, message: error.message, stack: error.stack }
        : { error: String(error) };

    const fullContext = { ...errorDetails, ...context };
    const redactedContext = redactPII(fullContext) as LogContext;
    const formatted = this.formatMessage("error", message, redactedContext);
    console.error(formatted);
  }
}

export const logger = new Logger();
